'use client';

/**
 * useChartColors Hook
 *
 * Provides theme-aware chart colors for data visualization libraries
 * like Recharts, Chart.js, etc.
 */

import { useTheme } from '@mui/material/styles';
import { useMemo } from 'react';
import { type ChartColorPalette, type ChartThemeMode, getChartColors } from '../theme/palette';

export interface UseChartColorsReturn extends ChartColorPalette {
  /**
   * Current theme mode used for colors
   */
  themeMode: ChartThemeMode;
}

/**
 * Hook to get theme-aware chart colors
 *
 * @example
 * ```tsx
 * function MyChart() {
 *   const colors = useChartColors();
 *
 *   return (
 *     <LineChart>
 *       <Line stroke={colors.primary} />
 *       <Line stroke={colors.success} />
 *     </LineChart>
 *   );
 * }
 * ```
 */
export function useChartColors(): UseChartColorsReturn {
  const theme = useTheme();

  const colors = useMemo(() => {
    // Determine theme mode from MUI theme palette mode and background
    // This handles our custom themes (twilight, midnight) by checking background color
    let themeMode: ChartThemeMode = theme.palette.mode as ChartThemeMode;

    // Detect twilight theme by its specific background color
    if (theme.palette.mode === 'dark' && theme.palette.background.default === '#0a1019') {
      themeMode = 'twilight';
    }
    // Detect midnight theme by its specific background color
    else if (theme.palette.mode === 'dark' && theme.palette.background.default === '#0a0a0a') {
      themeMode = 'midnight';
    }

    const chartColors = getChartColors(themeMode);

    return {
      ...chartColors,
      themeMode,
    };
  }, [theme.palette.mode, theme.palette.background.default]);

  return colors;
}

export default useChartColors;
